import React, {createContext, useCallback, useContext, useMemo, useState} from 'react';
import type {
    OpenDialogOptions,
    PageDialogContextValue,
    PageDialogEntry,
    PageDialogProviderProps,
} from './types';

const PageDialogContext = createContext<PageDialogContextValue | null>(null);

/**
 * Page/module dialog host: single mount point, open/close from anywhere under the provider, optional chaining.
 *
 * Use for feature-local dialogs. Keep Redux dialogs for true global/cross-feature types
 * (confirmation, notes, tasks, etc.).
 */
export const PageDialogProvider = ({
    children,
    registry = {},
}: PageDialogProviderProps): React.ReactElement => {
    const [stack, setStack] = useState<PageDialogEntry[]>([]);

    /**
     * Opens a dialog by registry type. Pass `chain: true` to push without closing the current top.
     */
    const openDialog = useCallback((
        type: string,
        props: Record<string, unknown> = {},
        {chain = false}: OpenDialogOptions = {},
    ): string => {
        const id = `${type}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        setStack((prev) => {
            const entry: PageDialogEntry = {id, type, props};
            if (chain || !prev.length) {
                return [...prev, entry];
            }
            return [...prev.slice(0, -1), entry];
        });
        return id;
    }, []);

    /**
     * Closes the top dialog, or a specific id when provided.
     */
    const closeDialog = useCallback((id?: string) => {
        setStack((prev) => {
            if (!prev.length) {
                return prev;
            }
            if (!id) {
                return prev.slice(0, -1);
            }
            return prev.filter((entry) => entry.id !== id);
        });
    }, []);

    /**
     * Closes current top then opens the next type (sequential chain).
     */
    const chainDialog = useCallback((type: string, props: Record<string, unknown> = {}) => {
        setStack((prev) => {
            const next = prev.length ? prev.slice(0, -1) : prev;
            const id = `${type}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
            return [...next, {id, type, props}];
        });
    }, []);

    /**
     * Clears the entire dialog stack.
     */
    const closeAll = useCallback(() => {
        setStack([]);
    }, []);

    const value = useMemo<PageDialogContextValue>(() => ({
        openDialog,
        closeDialog,
        chainDialog,
        closeAll,
        stack,
    }), [chainDialog, closeAll, closeDialog, openDialog, stack]);

    return (
        <PageDialogContext.Provider value={value}>
            {children}
            {stack.map((entry, index) => {
                const DialogComponent = registry[entry.type];
                if (!DialogComponent) {
                    return null;
                }
                const isTop = index === stack.length - 1;
                return (
                    <DialogComponent
                        key={entry.id}
                        {...(entry.props ?? {})}
                        open={isTop}
                        onClose={() => closeDialog(entry.id)}
                        dialogId={entry.id}
                    />
                );
            })}
        </PageDialogContext.Provider>
    );
};

/**
 * Access page dialog host APIs.
 */
export const usePageDialogs = (): PageDialogContextValue => {
    const ctx = useContext(PageDialogContext);
    if (!ctx) {
        throw new Error('usePageDialogs must be used within PageDialogProvider');
    }
    return ctx;
};

export default PageDialogProvider;
